import type { ReactElement } from "react";
import type { MetricGranularity } from "./fixtures";

/** Dashboard sections already established by ISSUE-352, not API metric names. */
export const ANALYTICS_SURFACES = [
  "journey",
  "activation",
  "retention",
  "core-engagement",
  "users-registered",
  "documents-indexed",
] as const;

export type AnalyticsSurface = (typeof ANALYTICS_SURFACES)[number];
export type AnalyticsQualification = "available" | "partial" | "suppressed" | "incomplete" | "unavailable";

/**
 * The metadata ISSUE-350 has explicitly committed to publish. `range` and
 * `scope` intentionally remain opaque until that issue accepts its wire
 * representation: the browser must not manufacture either one.
 */
export interface AnalyticsLiveContext {
  readonly range: unknown;
  readonly granularity: MetricGranularity;
  readonly coverageStartAt: string;
  readonly computedAt: string;
  /** Adapter-owned labels from ISSUE-350's approved response; never derived in the browser. */
  readonly rangeLabel: string;
  readonly granularityLabel: string;
}

/** A qualified surface from an approved aggregate adapter, never raw payload. */
export type AnalyticsQualifiedSurface =
  | {
      readonly qualification: "available" | "partial";
      readonly scope: unknown;
      /** Contract-owned qualification label, displayed without browser inference. */
      readonly scopeLabel: string;
      /** Only an ISSUE-350-approved adapter may supply a local renderable aggregate. */
      readonly aggregate: ReactElement;
    }
  | {
      readonly qualification: "suppressed" | "incomplete" | "unavailable";
      readonly scope: unknown;
      /** Contract-owned qualification label, displayed without browser inference. */
      readonly scopeLabel: string;
    };

export interface AnalyticsLiveDashboard {
  readonly context: AnalyticsLiveContext;
  readonly surfaces: Readonly<Record<AnalyticsSurface, AnalyticsQualifiedSurface>>;
}

/**
 * UI-owned state at the analytics boundary. It intentionally has no HTTP
 * shape: ISSUE-350 is the sole owner of the aggregate response contract.
 * Until that contract is accepted, a live adapter cannot be configured.
 */
export type AnalyticsDashboardState =
  | { readonly kind: "no-live-source" }
  | { readonly kind: "fixture-test-only" }
  | { readonly kind: "loading"; readonly retry?: () => void }
  | { readonly kind: "range-no-data" }
  | {
      readonly kind: "error";
      readonly retry: () => void;
      readonly affectedSurfaces?: readonly AnalyticsSurface[];
      readonly dashboard?: AnalyticsLiveDashboard;
    }
  | { readonly kind: "live"; readonly dashboard: AnalyticsLiveDashboard };

export interface AnalyticsDataSource {
  readonly state: AnalyticsDashboardState;
  readonly selectPeriod: (period: MetricGranularity) => void;
}

/**
 * Production's only source while ISSUE-350 has no delivered contract. It does
 * not fetch, retry, or fall back to fixtures.
 */
export function createNoLiveAnalyticsDataSource(
  selectPeriod: (period: MetricGranularity) => void,
): AnalyticsDataSource {
  return { state: { kind: "no-live-source" }, selectPeriod };
}

/** Test-harness-only fixture source. Production code must never call this. */
export function createFixtureAnalyticsDataSource(
  selectPeriod: (period: MetricGranularity) => void,
): AnalyticsDataSource {
  return { state: { kind: "fixture-test-only" }, selectPeriod };
}

/**
 * Reserved for ISSUE-350 integration. There is deliberately no transport,
 * endpoint, response schema, or runtime switch in ISSUE-428.
 */
export const liveAggregateSourceIsDisabled = true;
